import { useEffect, useState } from 'react';
/// <reference types="../types/css-modules" />
import style from '../styles/AccountEdit.module.css';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import ProfilePictureUpload from '../components/ProfilePictureUpload';
import { useAlertStore } from '../utils/alert';
import { useBannerStore } from '../utils/banner';
import { useAuth } from '../contexts/AuthContext';

/**
 * Page component for editing a group member account.
 * Retrieves current account data and autofills relevant fields.
 * Calls global alert for response messages.
 */
export function GroupMemberAccountEdit() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const [formData, setFormData] = useState({
    firstName: null as string | null,
    lastName: null as string | null,
    pronouns: null as string | null,
    email: null as string | null,
    tel: null as string | null,
    username: null as string | null,
    profilePictureUrl: null as string | null,
  });

  const profileQuery = trpc.member.getMyProfile.useQuery(undefined, {
    enabled: !!session,
    retry: false,
  });

  const fallbackProfilePictureUrl =
    formData.profilePictureUrl ?? profileQuery.data?.profile_picture_url ?? '';

  const showBanner = useBannerStore((state) => state.showBanner);
  const clearBanner = useBannerStore((state) => state.clearBanner);

  useEffect(() => {
    showBanner({
      icon: fallbackProfilePictureUrl,
      header: 'YOUR ACCOUNT',
      buttons: {
        View: '/group-member/profile',
        Edit: '/group-member/edit',
        Settings: '/group-member/settings',
      },
      selected: 'Edit',
    });
    return () => clearBanner();
  }, [fallbackProfilePictureUrl, showBanner, clearBanner]);

  const handleFormData = (field: string, data: string) => {
    setFormData((oldData) => ({ ...oldData, [field]: data }));
  };

  const updateProfile = trpc.member.updateMyProfile.useMutation({
    onSuccess: (data) => {
      if (data.updated) {
        useAlertStore.getState().showAlert({
          header: 'Success',
          body: 'Your profile has been updated.',
          button: 'Ok',
        });
        profileQuery.refetch();
      } else {
        useAlertStore.getState().showAlert({
          header: 'Error',
          body: 'No changes detected.',
          button: 'Ok',
        });
        profileQuery.refetch();
      }
    },
    onError: (_error) => {
      useAlertStore.getState().showAlert({
        header: 'Error',
        body: '{$error.message}',
        button: 'Ok',
      });
    },
  });

  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updates: {
      first_name?: string;
      last_name?: string;
      pronouns?: string;
      profile_picture_url?: string;
    } = {};

    const firstName = formData.firstName ?? profileQuery.data?.first_name ?? '';
    const lastName = formData.lastName ?? profileQuery.data?.last_name ?? '';
    const pronouns = formData.pronouns ?? profileQuery.data?.pronouns ?? '';
    const profilePictureUrl =
      formData.profilePictureUrl ?? profileQuery.data?.profile_picture_url ?? '';

    if (firstName.trim()) updates.first_name = firstName.trim();
    if (lastName.trim()) updates.last_name = lastName.trim();
    if (pronouns.trim()) updates.pronouns = pronouns.trim();
    if (profilePictureUrl.trim()) updates.profile_picture_url = profilePictureUrl.trim();

    updateProfile.mutate(updates);
  };

  if (profileQuery.isLoading) {
    return (
      <div className={style.page}>
        <p
          style={{
            padding: '2rem',
            fontFamily: 'Comfortaa, sans-serif',
            color: 'rgba(45,79,43,0.5)',
          }}
        >
          Loading…
        </p>
      </div>
    );
  }

  return (
    <div className={style.page}>
      {/* ── Header strip ── */}
      <div className={style.headerStrip}>
        <p className={style.pageTitle}>Edit Profile</p>
        <p className={style.pageSub}>Update your personal information</p>
      </div>

      {/* ── Form ── */}
      <form className={style.form} onSubmit={handleProfileSubmit}>
        {/* Profile picture */}
        <div className={style.profileContainer}>
          <ProfilePictureUpload
            currentUrl={fallbackProfilePictureUrl || null}
            userId={session?.user?.id || ''}
            onUploadComplete={(url) => {
              handleFormData('profilePictureUrl', url);
              updateProfile.mutate({ profile_picture_url: url });
            }}
            size={100}
            fallbackLetter={(
              profileQuery.data?.first_name ||
              profileQuery.data?.username ||
              '?'
            ).charAt(0)}
          />
        </div>

        {/* Personal info section */}
        <div className={style.sectionCard}>
          <div className={style.sectionHeader}>
            <div className={style.sectionDot} />
            Personal info
          </div>
          <div className={style.sectionBody}>
            <div className={style.fieldRow}>
              <label htmlFor="FirstName">First name</label>
              <input
                id="FirstName"
                name="FirstName"
                value={formData.firstName ?? profileQuery.data?.first_name ?? ''}
                onChange={(e) => handleFormData('firstName', e.target.value)}
              />
            </div>

            <div className={style.fieldRow}>
              <label htmlFor="LastName">Last name</label>
              <input
                id="LastName"
                name="LastName"
                value={formData.lastName ?? profileQuery.data?.last_name ?? ''}
                onChange={(e) => handleFormData('lastName', e.target.value)}
              />
            </div>

            <div className={style.fieldRow}>
              <label htmlFor="Pronouns">Pronouns</label>
              <select
                id="Pronouns"
                name="Pronouns"
                value={formData.pronouns ?? profileQuery.data?.pronouns ?? ''}
                onChange={(e) => handleFormData('pronouns', e.target.value)}
              >
                <option value="">-- Choose one --</option>
                <option value="He/Him">He/Him</option>
                <option value="She/Her">She/Her</option>
                <option value="They/Them">They/Them</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
        </div>

        {/* Contact section */}
        <div className={style.sectionCard}>
          <div className={style.sectionHeader}>
            <div className={style.sectionDot} />
            Contact
          </div>
          <div className={style.sectionBody}>
            <div className={style.fieldRow}>
              <label htmlFor="Email">Email</label>
              <input
                id="Email"
                name="Email"
                type="email"
                value={formData.email ?? session?.user?.email ?? ''}
                onChange={(e) => handleFormData('email', e.target.value)}
              />
            </div>

            <div className={style.fieldRow}>
              <label htmlFor="Phone">Phone</label>
              <input
                id="Phone"
                name="Phone"
                type="tel"
                value={formData.tel ?? session?.user?.phone ?? ''}
                onChange={(e) => handleFormData('tel', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className={style.buttonContainer}>
          <button
            type="button"
            className={style.cancelBtn}
            onClick={() => navigate('/group-member/profile')}
          >
            Cancel
          </button>
          <button type="submit" className={style.submitBtn}>
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}
